import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Logo360 } from "@/components/Logo";
import { LoginPage, RegisterPage, ForgotPage, ResetPage } from "@/pages/Auth";
import OnboardingPage from "@/pages/Onboarding";
import DashboardPage from "@/pages/Dashboard";
import ServicesPage from "@/pages/Services";
import ServiceEditor from "@/pages/ServiceEditor";
import PriceCard from "@/pages/PriceCard";
import MaterialsPage from "@/pages/Materials";
import FixedCostsPage from "@/pages/FixedCosts";
import SimulatorPage from "@/pages/Simulator";
import SettingsPage from "@/pages/Settings";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-ivory">
      <div className="animate-pulse"><Logo360 size={64} /></div>
    </div>
  );
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AccessDenied() {
  const { t, config, logout } = useAuth();
  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center p-6">
      <div className="max-w-md text-center bg-white rounded-2xl border border-brand-line p-8" data-testid="access-denied">
        <Logo360 size={56} />
        <h1 className="mt-4 font-serif text-2xl font-medium text-brand-anthracite">{t("pro.accessTitle")}</h1>
        <p className="mt-2 text-brand-taupe">{t("pro.accessDesc")}</p>
        <div className="mt-6 flex flex-col gap-2">
          {config.website_url && <a href={config.website_url} className="rounded-xl bg-brand-anthracite text-brand-ivory py-3 font-medium" data-testid="access-cta">{t("pro.accessCta")}</a>}
          <button onClick={logout} className="text-sm text-brand-taupe hover:text-brand-anthracite" data-testid="access-logout">{t("auth.logout")}</button>
        </div>
      </div>
    </div>
  );
}

// Distinct from AccessDenied ("NOT ENTITLED"): this is "STATUS UNAVAILABLE".
// Both fail closed for product access; this one offers a retry because the
// backend may simply be unreachable.
function EntitlementUnavailable() {
  const { t, entitled, refreshEntitlement, logout } = useAuth();
  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center p-6">
      <div className="max-w-md text-center bg-white rounded-2xl border border-brand-line p-8" data-testid="entitlement-unavailable">
        <Logo360 size={56} />
        <h1 className="mt-4 font-serif text-2xl font-medium text-brand-anthracite">{t("pro.accessTitle")}</h1>
        <p className="mt-2 text-brand-taupe">{t("auth.entitlementUnavailable")}</p>
        <div className="mt-6 flex flex-col gap-2">
          {entitled === "error" && (
            <button onClick={refreshEntitlement} className="rounded-xl bg-brand-anthracite text-brand-ivory py-3 font-medium" data-testid="entitlement-retry">
              {t("auth.retry")}
            </button>
          )}
          <button onClick={logout} className="text-sm text-brand-taupe hover:text-brand-anthracite" data-testid="entitlement-logout">{t("auth.logout")}</button>
        </div>
      </div>
    </div>
  );
}

function ProtectedApp({ children }) {
  const { user, entitled, config } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  if (config.access_mode === "entitlement") {
    if (entitled === false) return <AccessDenied />;
    if (entitled === "error") return <EntitlementUnavailable />;
    if (entitled === null) return <FullScreenLoader />; // verification in progress
  }
  return <Layout>{children}</Layout>;
}

// Authenticated but rendered without the app chrome (for clean printing).
function ProtectedBare({ children }) {
  const { user, entitled, config } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  if (config.access_mode === "entitlement") {
    if (entitled === false) return <AccessDenied />;
    if (entitled === "error") return <EntitlementUnavailable />;
    if (entitled === null) return <FullScreenLoader />;
  }
  return children;
}

function OnboardingGate() {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.onboarding_completed) return <Navigate to="/" replace />;
  return <OnboardingPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot" element={<PublicRoute><ForgotPage /></PublicRoute>} />
      <Route path="/reset" element={<PublicRoute><ResetPage /></PublicRoute>} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/" element={<ProtectedApp><DashboardPage /></ProtectedApp>} />
      <Route path="/services" element={<ProtectedApp><ServicesPage /></ProtectedApp>} />
      <Route path="/services/new" element={<ProtectedApp><ServiceEditor /></ProtectedApp>} />
      <Route path="/services/:id" element={<ProtectedApp><ServiceEditor /></ProtectedApp>} />
      <Route path="/services/:id/price-card" element={<ProtectedBare><PriceCard /></ProtectedBare>} />
      <Route path="/materials" element={<ProtectedApp><MaterialsPage /></ProtectedApp>} />
      <Route path="/fixed-costs" element={<ProtectedApp><FixedCostsPage /></ProtectedApp>} />
      <Route path="/simulator" element={<ProtectedApp><SimulatorPage /></ProtectedApp>} />
      <Route path="/settings" element={<ProtectedApp><SettingsPage /></ProtectedApp>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
